import React, { useState } from 'react';
import { initCaseFormChange, setFormChanged } from 'app/shared/reducers/case-form-change';
import { Field, FormikProvider, useFormik } from 'formik';
import { useDispatch } from 'react-redux';
import { Button } from 'edozvola-frontend-lib/dist/ui/components';
import CasePublicAuthorityForm from 'app/edozvola/cases/special-regulations-compliance/case-public-authority-form';
import CasePublicAuthorityTable from 'app/edozvola/cases/special-regulations-compliance/case-public-authority-table';
import { specialRegulationsComplianceInitialData } from 'app/edozvola/cases/special-regulations-compliance/model/special-regulations-compliance.model';
import { Col, Row } from 'reactstrap';
import { Input, Label } from 'edozvola-frontend-lib/dist/ui';
import { FormikDatepicker } from 'edozvola-frontend-lib/dist/ui/components/datepicker/FormikDatepicker';
import { APP_DATE_FORMAT } from 'app/config/constants';
import {
  copyPublicAuthoritiesFromCaseQuery,
  fetchSpecialRegulationsComplianceQuery,
  saveSpecialRegulationsComplianceQuery,
} from 'app/edozvola/cases/special-regulations-compliance/service/special-regulations-compliance.queries';
import { useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { casePublicAuthorityCaseUuidToCopyFromSchema } from 'app/edozvola/cases/special-regulations-compliance/model/special-regulations-compliance.schema';
import { fetchMethodOfApplicationOptionsQuery } from 'app/edozvola/codebooks/codebooks.queries';
import FormikValueObserver from 'app/shared/formik-observer/formik-observer';
import { getCaseContext } from 'app/shared/util/context-util';

const SpecialRegulationsCompliance = () => {
  const { id: uuid } = useParams<{ id: string }>();

  const dispatch = useDispatch();
  const { sidebarItemPair } = getCaseContext();

  const [idCasePublicAuthority, setIdCasePublicAuthority] = useState<string | null>(null);
  const [readOnly, setReadOnly] = useState(false);

  const { data: methodOfApplicationOptions, isLoading: loadingMethodOfApplicationOptions } = fetchMethodOfApplicationOptionsQuery();
  const { data: specialRegulationsComplianceData, isLoading: specialRegulationsComplianceDataLoading } =
    fetchSpecialRegulationsComplianceQuery(uuid);

  const formik = useFormik({
    initialValues: specialRegulationsComplianceData || specialRegulationsComplianceInitialData,
    onSubmit: values => saveSpecialRegulationsComplianceMutation(values),
    enableReinitialize: true,
    validateOnMount: true,
  });

  const copyJptFormik = useFormik({
    initialValues: { caseUuidToCopyFrom: '' },
    validationSchema: casePublicAuthorityCaseUuidToCopyFromSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: values => copyCasePublicAuthorities(values.caseUuidToCopyFrom),
  });

  const { mutate: copyCasePublicAuthorities, isPending: copyCasePublicAuthoritiesPending } = copyPublicAuthoritiesFromCaseQuery(
    uuid,
    copyJptFormik.resetForm,
  );

  const { mutate: saveSpecialRegulationsComplianceMutation, isPending: savingSpecialRegulationsCompliance } =
    saveSpecialRegulationsComplianceQuery(uuid, formik.resetForm);

  const openNewCasePublicAuthority = () => {
    setIdCasePublicAuthority('');
    setReadOnly(false);
  };

  return (
    <>
      <FormikProvider value={formik}>
        <FormikValueObserver
          onChange={dirty => {
            if (dirty.dirty) {
              dispatch(setFormChanged(true));
            } else {
              dispatch(initCaseFormChange());
            }
          }}
        />
        <div className={'form-header mb-4'}>Javnopravna tijela koje se pozivaju putem eKonferencije</div>
        {!sidebarItemPair?.readOnly && (
          <>
            <div className={'d-flex justify-content-end pb-4 gap-2'}>
              <div className={'case-copy-from-other-case'}>
                <Input
                  placeholder={'Unesite šifru predmeta'}
                  maxLength={255}
                  addonAppend={
                    copyCasePublicAuthoritiesPending ? (
                      <FontAwesomeIcon icon="spinner" spin />
                    ) : (
                      <FontAwesomeIcon
                        aria-disabled={
                          (!!copyJptFormik.errors && !!copyJptFormik.errors.caseUuidToCopyFrom) ||
                          copyJptFormik.values.caseUuidToCopyFrom === ''
                        }
                        className={
                          (!!copyJptFormik.errors && !!copyJptFormik.errors.caseUuidToCopyFrom) ||
                          copyJptFormik.values.caseUuidToCopyFrom === ''
                            ? 'disabled'
                            : ''
                        }
                        icon="copy"
                      />
                    )
                  }
                  value={copyJptFormik.values.caseUuidToCopyFrom}
                  onChange={event => {
                    copyJptFormik.setFieldValue('caseUuidToCopyFrom', event.target.value);

                    if (copyJptFormik.submitCount > 0) {
                      copyJptFormik.setErrors({});
                    }
                  }}
                  addonAppendClick={() => {
                    if (!copyCasePublicAuthoritiesPending) {
                      copyJptFormik.handleSubmit();
                    }
                  }}
                  error={copyJptFormik.errors && copyJptFormik.errors.caseUuidToCopyFrom ? copyJptFormik.errors.caseUuidToCopyFrom : ''}
                />
              </div>
              <div>
                <Button label={'Dodaj JPT'} color={'info'} prependIcon={'plus'} size={'lg'} onClick={openNewCasePublicAuthority} />
              </div>
            </div>
          </>
        )}
        <>
          <CasePublicAuthorityTable setCasePublicAuthority={setIdCasePublicAuthority} setReadOnly={setReadOnly} />
          {idCasePublicAuthority !== null && (
            <CasePublicAuthorityForm
              idCasePublicAuthority={idCasePublicAuthority}
              setIdCasePublicAuthority={setIdCasePublicAuthority}
              readOnly={readOnly}
            />
          )}
          <hr />
          <div className={'mt-4 d-flex flex-column gap-3'}>
            <div className={'form-header'}>Podaci o eKonferenciji</div>
            <Row>
              <Col md={6}>
                <Label labelText={'Početak konferencije'} />
                <Field
                  id="conferenceStart"
                  name="conferenceStart"
                  render={({ field }) => (
                    <FormikDatepicker field={field} setFieldValue={formik.setFieldValue} dateFormat={APP_DATE_FORMAT} disabled={true} />
                  )}
                />
              </Col>
              <Col md={6}>
                <Label labelText={'Završetak konferencije'} />
                <Field
                  id="conferenceEnd"
                  name="conferenceEnd"
                  render={({ field }) => (
                    <FormikDatepicker field={field} setFieldValue={formik.setFieldValue} dateFormat={APP_DATE_FORMAT} disabled={true} />
                  )}
                />
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Label labelText={'Trajanje konferencije'} />
                <Input value={formik.values.conferenceDuration} disabled={true} />
              </Col>
            </Row>
          </div>
        </>
      </FormikProvider>
    </>
  );
};

export default SpecialRegulationsCompliance;
